import { trackPendoEvent } from 'utils/pendo'
import { getPlanName } from 'utils/plans'
import { getRoleNameById } from 'utils/roles'

import {
  selectActualCompanyId,
  selectActualIsPartner,
  selectActualLicenses,
  selectRegion,
} from 'containers/App/selectors'
import { DELIVERY, getCampaignTypeByState } from 'containers/AwarenessCenter/constants'
import { APPROVE_CAMPAIGN_ASYNC } from 'containers/AwarenessCenter/WizardCampaign/steps/constants'
import {
  selectIsTournament,
  selectParticipants,
  selectSelectedDelivery,
} from 'containers/AwarenessCenter/WizardCampaign/steps/selectors'
import { SUBMIT_CUSTOMIZED_AUTO_SYNC_ASYNC } from 'containers/CustomizedAutoSync/constants'
import { AS_GD_ASYNC, AS_O365_ASYNC } from 'containers/Mailboxes/constants'
import { MAP_INCIDENT_TYPE_TO_NAME } from 'containers/ResolutionCenter/constants'
import { REPORT_STATE_CHANGE_ASYNC } from 'containers/ResolutionCenter/Report/constants'
import { UPDATE_SERVER_SIDE_ASYNC } from 'containers/Settings/EmailServer/constants'
import { ENABLE_INTEGRATION_REQUEST_ASYNC } from 'containers/Settings/EmailServer/Gsuite/constants'
import { ADD_USER_REQUEST_ASYNC } from 'containers/Settings/Users/constants'
import {
  MAP_WHITELIST_TYPE_TO_NAME,
  WHITELIST_ADD_ASYNC,
  WHITELIST_EDIT_ASYNC,
  WHITELIST_FOR_TYPES,
} from 'containers/Settings/Whitelist/constants'

export const USAGE_OUTCOMES = {
  CAMPAIGN_LAUNCHED: 'awareness.campaign.launched',
  INTEGRATION_CONNECTED: 'settings.integration.connected',
  MAILBOXES_IMPORTED: 'mailboxes.imported',
  ALLOWLIST_SAVED: 'settings.allowlist.saved',
  USER_INVITED: 'settings.user.invited',
  INCIDENTS_CLASSIFIED: 'incidents.classified',
}

const BLOCKED_PROPERTY =
  /(^|_)(email|e-mail|name|filename|subject|body|message|comment|domain|address|token|secret|password|sender|recipient)($|_)/i

const VERDICTS = new Set(['phishing', 'spam', 'safe', 'delete'])

const pending = {
  allowlist: null,
  invite: null,
}

export function resetPendingUsageOutcomes() {
  pending.allowlist = null
  pending.invite = null
}

export function usageOutcomeProperties(properties = {}) {
  const safe = {}
  Object.keys(properties).forEach((key) => {
    if (BLOCKED_PROPERTY.test(key)) return
    const value = properties[key]
    if (value == null) return
    if (typeof value === 'boolean' || typeof value === 'number') {
      safe[key] = value
      return
    }
    if (typeof value === 'string' && value.length <= 64 && !value.includes('@')) {
      safe[key] = value
    }
  })
  return safe
}

export function trackUsageOutcome(eventName, properties) {
  trackPendoEvent(eventName, usageOutcomeProperties(properties))
}

function usageContext(state) {
  const properties = {}
  try {
    const companyId = selectActualCompanyId(state)
    if (typeof companyId === 'number' || typeof companyId === 'string') {
      properties.company_id = companyId
    }
    properties.plan = getPlanName(selectActualLicenses(state)?.plan)
    properties.is_partner = Boolean(selectActualIsPartner(state))
    const region = selectRegion(state)
    if (typeof region === 'string' && region) properties.region = region
  } catch {
    return properties
  }
  return properties
}

function roleName(roleId) {
  try {
    return getRoleNameById(roleId)
  } catch {
    return undefined
  }
}

function allowlistProperties(data = {}, isEdit) {
  const typeName = MAP_WHITELIST_TYPE_TO_NAME[data.type]
  const scopeKey = Object.keys(WHITELIST_FOR_TYPES).find(
    (key) => WHITELIST_FOR_TYPES[key] === data.scope
  )
  return {
    rule_type: typeName ? typeName.toLowerCase() : undefined,
    scope: scopeKey ? scopeKey.toLowerCase() : undefined,
    is_edit: Boolean(isEdit),
    external_campaigns: Boolean(data.external_campaigns),
    ignore_auth: Boolean(data.ignore_auth),
    has_expiration: Boolean(data.expiration_date),
  }
}

function campaignProperties(state) {
  const properties = { campaign_type: getCampaignTypeByState(state) }
  try {
    const count = selectParticipants(state)?.meta?.count
    if (typeof count === 'number') properties.participant_count = count
    const delivery = selectSelectedDelivery(state)
    if (delivery === DELIVERY.EMAIL) properties.delivery = 'email'
    else if (delivery === DELIVERY.SMS) properties.delivery = 'sms'
    properties.is_tournament = Boolean(selectIsTournament(state))
  } catch {
    // Wizard selectors throw until the campaign slice is mounted.
  }
  return properties
}

function rememberPending(action) {
  if (
    action.type === WHITELIST_ADD_ASYNC.INITIATE ||
    action.type === WHITELIST_EDIT_ASYNC.INITIATE
  ) {
    pending.allowlist = allowlistProperties(
      action.payload?.data,
      action.type === WHITELIST_EDIT_ASYNC.INITIATE
    )
  }
  if (action.type === ADD_USER_REQUEST_ASYNC.INITIATE) {
    pending.invite = { role: roleName(action.payload?.user?.role) }
  }
}

function clearPending(action) {
  if (
    action.type === WHITELIST_ADD_ASYNC.ERROR ||
    action.type === WHITELIST_ADD_ASYNC.CANCEL ||
    action.type === WHITELIST_EDIT_ASYNC.ERROR ||
    action.type === WHITELIST_EDIT_ASYNC.CANCEL
  ) {
    pending.allowlist = null
  }
  if (
    action.type === ADD_USER_REQUEST_ASYNC.ERROR ||
    action.type === ADD_USER_REQUEST_ASYNC.CANCEL
  ) {
    pending.invite = null
  }
}

function outcomeForSuccess(action, state) {
  const context = usageContext(state)

  if (action.type === APPROVE_CAMPAIGN_ASYNC.SUCCESS) {
    return {
      event: USAGE_OUTCOMES.CAMPAIGN_LAUNCHED,
      properties: { ...context, ...campaignProperties(state) },
    }
  }

  if (action.type === ENABLE_INTEGRATION_REQUEST_ASYNC.SUCCESS) {
    return {
      event: USAGE_OUTCOMES.INTEGRATION_CONNECTED,
      properties: { ...context, integration_type: 'gsuite', action: 'connected' },
    }
  }

  if (action.type === UPDATE_SERVER_SIDE_ASYNC.SUCCESS) {
    return {
      event: USAGE_OUTCOMES.INTEGRATION_CONNECTED,
      properties: { ...context, integration_type: 'exchange', action: 'configured' },
    }
  }

  if (action.type === AS_O365_ASYNC.SUCCESS || action.type === AS_GD_ASYNC.SUCCESS) {
    return {
      event: USAGE_OUTCOMES.MAILBOXES_IMPORTED,
      properties: {
        ...context,
        source: 'directory_sync',
        vendor: action.type === AS_O365_ASYNC.SUCCESS ? 'office365' : 'gsuite',
      },
    }
  }

  if (action.type === SUBMIT_CUSTOMIZED_AUTO_SYNC_ASYNC.SUCCESS) {
    return {
      event: USAGE_OUTCOMES.MAILBOXES_IMPORTED,
      properties: { ...context, source: 'customized_sync' },
    }
  }

  if (action.type === WHITELIST_ADD_ASYNC.SUCCESS || action.type === WHITELIST_EDIT_ASYNC.SUCCESS) {
    const saved = pending.allowlist
    pending.allowlist = null
    return {
      event: USAGE_OUTCOMES.ALLOWLIST_SAVED,
      properties: { ...context, ...saved },
    }
  }

  if (action.type === ADD_USER_REQUEST_ASYNC.SUCCESS) {
    const invited = pending.invite
    pending.invite = null
    return {
      event: USAGE_OUTCOMES.USER_INVITED,
      properties: { ...context, ...invited },
    }
  }

  if (action.type === REPORT_STATE_CHANGE_ASYNC.SUCCESS) {
    const verdict = action.payload?.newState
    const reportType = MAP_INCIDENT_TYPE_TO_NAME[action.payload?.reportType]
    return {
      event: USAGE_OUTCOMES.INCIDENTS_CLASSIFIED,
      properties: {
        ...context,
        count: 1,
        verdict: VERDICTS.has(verdict) ? verdict : undefined,
        report_type: reportType,
      },
    }
  }

  return null
}

export const usageOutcomeMiddleware = (store) => (next) => (action) => {
  if (!action?.type) return next(action)

  rememberPending(action)
  const result = next(action)
  clearPending(action)

  const outcome = outcomeForSuccess(action, store.getState())
  if (outcome) trackUsageOutcome(outcome.event, outcome.properties)
  return result
}
